/**
 * SendFormPanel — assign a form to a contact.
 *
 * Features:
 *   - Contact typeahead search
 *   - Form selector dropdown
 *   - Expiry selector (24h / 48h / 72h / 1 week)
 *   - Send with confirmation + shows copy-able magic link
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  forms as formsApi,
  packages as packagesApi,
  ghl,
  ghlContactsUrl,
  FormSchema,
  PackageSummary,
  GHLContactResult,
  NotifiedVia,
} from '../../../lib/api.js';
import { useAuth } from '../../../lib/auth-context.js';

type SendType = 'form' | 'package';

interface Props {
  preselectedContactId?: string | undefined;
  onSent?: (() => void) | undefined;
}

const EXPIRY_OPTIONS = [
  { label: '24 hours', value: 24 },
  { label: '48 hours', value: 48 },
  { label: '72 hours (default)', value: 72 },
  { label: '1 week',  value: 168 },
];

export default function SendFormPanel({ preselectedContactId, onSent }: Props): React.ReactElement {
  const { user } = useAuth();
  const locationId = user?.locationId ?? '';
  const [sendType,  setSendType]  = useState<SendType>('form');
  const [schemas,   setSchemas]   = useState<FormSchema[]>([]);
  const [formId,    setFormId]    = useState('');
  const [pkgs,      setPkgs]      = useState<PackageSummary[]>([]);
  const [packageId, setPackageId] = useState('');
  const [expiry,    setExpiry]    = useState(72);
  const [contacts,  setContacts]  = useState<GHLContactResult[]>([]);
  const [noResults, setNoResults] = useState(false);
  const [query,     setQuery]     = useState('');
  const [selected,  setSelected]  = useState<GHLContactResult | null>(null);
  const [sending,   setSending]   = useState(false);
  const [magicLink, setMagicLink] = useState<string | null>(null);
  const [notifiedVia, setNotifiedVia] = useState<NotifiedVia>('none');
  const [error,     setError]     = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    formsApi.schemas.list().then((res) => {
      if (res.success && res.data) setSchemas(res.data);
    }).catch(() => {/* ignore */});
    packagesApi.list().then((res) => {
      if (res.success && res.data) setPkgs(res.data);
    }).catch(() => {/* ignore */});
  }, []);

  // Debounced contact search
  useEffect(() => {
    if (query.length < 2) { setContacts([]); setNoResults(false); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSearching(true);
      setNoResults(false);
      try {
        const res = await ghl.searchContacts(query);
        const found = res.success && res.data ? res.data.contacts : [];
        setContacts(found);
        setNoResults(found.length === 0);
      } catch {
        setContacts([]);
        setNoResults(true);
      } finally { setSearching(false); }
    }, 300);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [query]);

  const openAddContact = () => {
    if (!locationId) return;
    window.open(ghlContactsUrl(locationId), '_blank', 'noopener,noreferrer');
  };

  const handleSend = async () => {
    if (!selected) { setError('Please select a contact'); return; }
    if (sendType === 'form' && !formId)       { setError('Please select a form'); return; }
    if (sendType === 'package' && !packageId) { setError('Please select a package'); return; }

    const sendLabel = sendType === 'form'
      ? schemas.find((s) => s.id === formId)?.name
      : pkgs.find((p) => p.id === packageId)?.name;
    if (!confirm(`Send "${sendLabel}" to ${selected.firstName} ${selected.lastName}?`)) return;

    setSending(true); setError(null);
    try {
      const res = sendType === 'form'
        ? await formsApi.assign({ formId, contactGhlId: selected.id, expiresInHours: expiry })
        : await packagesApi.assign({ packageId, contactGhlId: selected.id, expiresInHours: expiry });
      if (res.success && res.data) {
        setNotifiedVia(res.data.notifiedVia ?? (res.data.notified ? 'sms' : 'none'));
        setMagicLink(res.data.magicLink);
        onSent?.();
      } else {
        setError(res.error ?? `Failed to send ${sendType}`);
      }
    } catch {
      setError(`Unexpected error sending ${sendType}`);
    } finally {
      setSending(false);
    }
  };

  if (magicLink) {
    return (
      <div style={styles.successBox}>
        <span style={{ fontSize: 28 }}>✅</span>
        <h3 style={styles.successTitle}>Form Sent!</h3>
        {notifiedVia !== 'none' ? (
          <p style={styles.successSub}>
            {notifiedVia === 'email_sms'
              ? 'The contact has been notified by email and text.'
              : notifiedVia === 'email'
              ? 'The contact has been notified by email.'
              : 'The contact has been notified by text.'}
          </p>
        ) : (
          <p style={{ ...styles.successSub, color: '#b45309' }}>
            We couldn’t notify the contact automatically. Copy the link below and send it to them directly.
          </p>
        )}
        <label style={styles.label}>Magic Link (backup)</label>
        <div style={styles.linkRow}>
          <input readOnly value={magicLink} style={styles.linkInput} />
          <button style={styles.copyBtn} onClick={() => navigator.clipboard.writeText(magicLink)}>Copy</button>
        </div>
        <button style={styles.resetBtn} onClick={() => { setMagicLink(null); setSelected(null); setFormId(''); setPackageId(''); }}>
          Send Another
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.heading}>Send a Form</h3>
      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* Contact picker */}
      <div style={styles.field}>
        <label style={styles.label}>Recipient</label>
        {selected ? (
          <div style={styles.selectedContact}>
            <span>{selected.firstName} {selected.lastName} — {selected.email}</span>
            <button style={styles.clearBtn} onClick={() => { setSelected(null); setQuery(''); }}>✕</button>
          </div>
        ) : (
          <>
            <input
              style={styles.input}
              placeholder="Search by name, email, or phone…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {searching && <p style={styles.hint}>Searching…</p>}
            {contacts.length > 0 && (
              <div style={styles.dropdown}>
                {contacts.map((c) => (
                  <button key={c.id} style={styles.dropdownItem} onClick={() => { setSelected(c); setContacts([]); setQuery(''); setNoResults(false); }}>
                    {c.firstName} {c.lastName}
                    <span style={styles.dropdownSub}>{c.email}</span>
                  </button>
                ))}
              </div>
            )}
            {!searching && noResults && (
              <div style={styles.noResults}>
                <span style={styles.noResultsText}>No contact found for “{query.trim()}”.</span>
                <button style={styles.addNewBtn} onClick={openAddContact} type="button" disabled={!locationId}>
                  + Add new contact
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Send-type toggle */}
      <div style={styles.field}>
        <label style={styles.label}>Send</label>
        <div style={styles.pillGroup}>
          <button
            type="button"
            style={{ ...styles.pill, ...(sendType === 'form' ? styles.pillActive : {}) }}
            onClick={() => setSendType('form')}
          >
            Single form
          </button>
          <button
            type="button"
            style={{ ...styles.pill, ...(sendType === 'package' ? styles.pillActive : {}) }}
            onClick={() => setSendType('package')}
          >
            Package
          </button>
        </div>
      </div>

      {/* Form / Package selector */}
      {sendType === 'form' ? (
        <div style={styles.field}>
          <label style={styles.label}>Form</label>
          <select style={styles.select} value={formId} onChange={(e) => setFormId(e.target.value)}>
            <option value="">Select a form…</option>
            {schemas.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      ) : (
        <div style={styles.field}>
          <label style={styles.label}>Package</label>
          <select style={styles.select} value={packageId} onChange={(e) => setPackageId(e.target.value)}>
            <option value="">Select a package…</option>
            {pkgs.map((p) => (
              <option key={p.id} value={p.id}>{p.name} ({p.itemCount} form{p.itemCount === 1 ? '' : 's'})</option>
            ))}
          </select>
        </div>
      )}

      {/* Expiry */}
      <div style={styles.field}>
        <label style={styles.label}>Link Expiry</label>
        <select style={styles.select} value={expiry} onChange={(e) => setExpiry(Number(e.target.value))}>
          {EXPIRY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <button style={styles.sendBtn} onClick={handleSend} disabled={sending}>
        {sending ? 'Sending…' : '🔒 Send Securely'}
      </button>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '20px', maxWidth: 480 },
  heading:   { margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: '#111827' },
  field:     { marginBottom: 14 },
  label:     { display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 },
  input:     { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 13, color: '#111827', boxSizing: 'border-box' as const },
  select:    { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 13, color: '#111827' },
  pillGroup: { display: 'flex', gap: 6 },
  pill:      { flex: 1, padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 999, background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  pillActive:{ background: '#2563eb', color: '#fff', border: '1px solid #2563eb' },
  hint:      { fontSize: 11, color: '#9ca3af', margin: '4px 0 0' },
  dropdown:  { border: '1px solid #d1d5db', borderRadius: 5, background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 160, overflowY: 'auto', marginTop: 2 },
  dropdownItem: { display: 'flex', flexDirection: 'column', width: '100%', padding: '7px 12px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13, color: '#111827' },
  dropdownSub: { fontSize: 11, color: '#6b7280' },
  noResults: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
    marginTop: 2, padding: '7px 10px',
    border: '1px solid #e5e7eb', borderRadius: 5, background: '#f9fafb',
  },
  noResultsText: { fontSize: 12, color: '#6b7280' },
  addNewBtn: {
    flexShrink: 0, background: '#eff6ff', color: '#2563eb',
    border: '1px solid #bfdbfe', borderRadius: 5, padding: '5px 10px',
    cursor: 'pointer', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap',
  },
  selectedContact: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', background: '#eff6ff', borderRadius: 5, fontSize: 13, color: '#2563eb' },
  clearBtn:  { background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 14 },
  sendBtn:   { width: '100%', padding: '10px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: 'pointer', marginTop: 4 },
  errorBanner: { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '8px 12px', color: '#b91c1c', fontSize: 13, marginBottom: 14 },
  successBox:  { padding: '32px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center', maxWidth: 380 },
  successTitle: { margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' },
  successSub: { margin: 0, fontSize: 13, color: '#6b7280' },
  linkRow:   { display: 'flex', gap: 6, width: '100%' },
  linkInput: { flex: 1, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 11, color: '#374151' },
  copyBtn:   { padding: '7px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 5, cursor: 'pointer', fontSize: 12 },
  resetBtn:  { marginTop: 8, padding: '8px 16px', background: 'none', border: '1px solid #d1d5db', borderRadius: 5, cursor: 'pointer', fontSize: 13, color: '#374151' },
};
